/**
 * Création et enregistrement des rubriques du PV ouvert, communs au panneau gauche
 * (Rubrics / StandardReports), à l'édition, au sommaire et à la discussion
 */
import axios from 'axios';
import { insertRubricsBefore } from './rubricDrop';

const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('authToken')}` });

export const createRubricId = (suffix = '') =>
  `rubric-${Date.now()}-${Math.floor(Math.random() * 10000)}${suffix}`;

/**
 * Rubrique du catalogue ou personnelle (« Mes rubriques ») -> données d'une nouvelle rubrique du document
 * @returns {{ title: string, content: string, version: string, generated: boolean }}
 */
export const rubricFromTemplate = (template) => {
  if (template.isUserRubric) {
    return { title: template.title, content: template.content || '', version: '', generated: true };
  }

  const base = {
    title: template.type === 'manual' ? (template['content-title'] || template.title) : template.title,
    version: template.version || ''
  };
  return template.type === 'intelligent'
    ? { ...base, content: '', generated: false }
    : { ...base, content: template.content || '', generated: true };
};

const rubricFromStandardRubric = (rubric) => {
  if (rubric.type === 'intelligent') {
    return { title: rubric.title, content: '', version: rubric.version || '', generated: false };
  }
  if (rubric.type === 'manual') {
    return { title: rubric.title, content: rubric.content || '', version: null, generated: true };
  }
  return { title: rubric.title, content: rubric.content || '', version: '', generated: true };
};

/**
 * Rubriques d'un canevas, dans l'ordre du canevas, prêtes à être ajoutées au document
 */
export const fetchStandardRubrics = async (standard) => {
  const { data } = await axios.get('/api/standards/rubrics', { params: { includeObsolete: true } });
  const catalogue = Array.isArray(data) ? data : [];
  return (standard.rubrics || [])
    .map((rubricId) => catalogue.find((rubric) => rubric.id === rubricId))
    .filter(Boolean)
    .map(rubricFromStandardRubric);
};

/**
 * Enregistre la liste complète des rubriques du PV et la diffuse aux utilisateurs connectés
 */
export const saveReportRubrics = async ({ reportId, report, socket, rubrics }) => {
  await axios.put(`/api/report/${reportId}/rubrics`, { rubrics }, { headers: authHeaders() });

  if (socket) {
    socket.emit('updateReport', {
      reportId: `${report.number}-${report.year}`,
      rubrics
    });
    socket.emit('broadcastReportUpdate', {
      number: report.number,
      year: report.year,
      rubrics
    });
  }
};

/**
 * Ajoute de nouvelles rubriques au PV ouvert avant beforeRubricId (en fin de document si null ou introuvable).
 * Repart des rubriques enregistrées sur le serveur pour ne pas écraser le travail des autres utilisateurs.
 * @param {Array<{ title, content, version, generated }>} rubrics
 * @returns {Promise<{ createdRubrics: Array, updatedRubrics: Array, insertedPosition: number|null, atEnd: boolean }>}
 */
export const addRubricsToReport = async ({ report, socket, rubrics, beforeRubricId = null }) => {
  const { data } = await axios.get('/api/report/search', {
    params: { number: report.number, year: report.year },
    headers: authHeaders()
  });
  if (!data?._id) {
    throw new Error('Impossible de récupérer les données du rapport');
  }

  const createdRubrics = rubrics.map((rubric, index) => ({
    ...rubric,
    id: createRubricId(rubrics.length > 1 ? `-${index}` : ''),
    isEditing: false,
    editedBy: null
  }));
  const updatedRubrics = insertRubricsBefore(data.rubrics || [], createdRubrics, beforeRubricId);

  await saveReportRubrics({ reportId: data._id, report, socket, rubrics: updatedRubrics });

  const insertedPosition = createdRubrics.length
    ? updatedRubrics.find((rubric) => rubric.id === createdRubrics[0].id).position
    : null;

  return {
    createdRubrics,
    updatedRubrics,
    insertedPosition,
    atEnd: insertedPosition !== null && insertedPosition + createdRubrics.length - 1 === updatedRubrics.length
  };
};
